import { type ReactNode } from "react";
import { Link } from "wouter";
import {
  Circle, Eye, Mail, Download, Copy, Edit, Trash2,
  Loader2, LayoutTemplate, Lock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { ProGate } from "@/components/pro-gate";
import { useAuth, getTrialInfo } from "@/hooks/use-auth";
import type { ProFeatureKey } from "@workspace/pricing";
import { PRO_FEATURES } from "@workspace/pricing";

export type StatusOption = { label: string; value: string; disabled?: boolean; description?: string };
export type PreviewOption = { label: string; description: string; icon: ReactNode; onClick: () => void };
export type ExportOption = { label: string; description: string; icon: ReactNode; onClick: () => void };

const STATUS_COLORS: Record<string, string> = {
  draft:     "#9ca3af",
  sent:      "#3b82f6",
  accepted:  "#22c55e",
  declined:  "#ef4444",
  converted: "#a855f7",
  paid:      "#22c55e",
  partial:   "#eab308",
  overdue:   "#ef4444",
  void:      "#9ca3af",
};

export interface EditorActionBarTourTargets {
  /** data-tour-target applied to the status (circle) button */
  statusButton?: string;
  /** data-tour-target applied to the preview (eye) button */
  previewButton?: string;
  /** data-tour-target applied to the send-email (mail) button */
  sendEmailButton?: string;
}

export interface EditorActionBarProps {
  statusOptions: StatusOption[];
  onStatusChange: (value: string) => void;
  currentStatus?: string;

  previewOptions: PreviewOption[];

  sendEmailFeature: ProFeatureKey;
  onSendEmail: () => void;
  sendEmailLabel?: string;

  exportOptions: ExportOption[];

  onDuplicate: () => void;
  isDuplicating?: boolean;
  duplicateLabel?: string;

  onEdit?: () => void;
  editLabel?: string;

  onDelete: () => void;
  deleteLabel?: string;

  onSaveAsTemplate?: () => void;
  convertDesktopSlot?: ReactNode;
  /** Optional tour-target overrides for specific buttons */
  tourTargets?: EditorActionBarTourTargets;
}

export function EditorActionBar({
  statusOptions,
  onStatusChange,
  currentStatus,
  previewOptions,
  sendEmailFeature,
  onSendEmail,
  sendEmailLabel = "Send via Email",
  exportOptions,
  onDuplicate,
  isDuplicating = false,
  duplicateLabel = "Duplicate",
  onEdit,
  editLabel = "Edit",
  onDelete,
  deleteLabel = "Delete",
  onSaveAsTemplate,
  convertDesktopSlot,
  tourTargets,
}: EditorActionBarProps) {
  const isSinglePreview = previewOptions.length === 1;
  const isSingleExport = exportOptions.length === 1;
  const { user } = useAuth();
  const { hasProAccess: isPro } = getTrialInfo(user);
  const circleColor = (currentStatus && STATUS_COLORS[currentStatus]) ?? "currentColor";

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Tooltip delayDuration={0}>
        <DropdownMenu>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <Button
                size="icon" className="h-10 w-10" variant="outline" aria-label="Change status"
                {...(tourTargets?.statusButton ? { "data-tour-target": tourTargets.statusButton } : {})}
              >
                <Circle className="w-5 h-5" strokeWidth={2.25} style={{ color: circleColor, fill: circleColor }} />
              </Button>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent>Change status</TooltipContent>
          <DropdownMenuContent align="end">
            {statusOptions.map((opt) => (
              <DropdownMenuItem
                key={opt.value}
                disabled={opt.disabled}
                onClick={() => !opt.disabled && onStatusChange(opt.value)}
                className={opt.description ? "flex-col items-start gap-0.5" : undefined}
              >
                <span>{opt.label}</span>
                {opt.description && <span className="text-xs text-muted-foreground">{opt.description}</span>}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </Tooltip>

      {isSinglePreview ? (
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>
            <Button
              size="icon" className="h-10 w-10" variant="outline" aria-label="Preview PDF"
              onClick={previewOptions[0].onClick}
              {...(tourTargets?.previewButton ? { "data-tour-target": tourTargets.previewButton } : {})}
            >
              <Eye className="w-5 h-5" strokeWidth={2.25} />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Preview PDF</TooltipContent>
        </Tooltip>
      ) : (
        <Tooltip delayDuration={0}>
          <DropdownMenu>
            <TooltipTrigger asChild>
              <DropdownMenuTrigger asChild>
                <Button
                  size="icon" className="h-10 w-10" variant="outline" aria-label="Preview PDF"
                  {...(tourTargets?.previewButton ? { "data-tour-target": tourTargets.previewButton } : {})}
                >
                  <Eye className="w-5 h-5" strokeWidth={2.25} />
                </Button>
              </DropdownMenuTrigger>
            </TooltipTrigger>
            <TooltipContent>Preview PDF</TooltipContent>
            <DropdownMenuContent align="end" className="w-56">
              {previewOptions.map((opt, i) => (
                <div key={i}>
                  {i > 0 && <DropdownMenuSeparator />}
                  <DropdownMenuItem onClick={opt.onClick}>
                    {opt.icon}
                    <div>
                      <p className="font-medium">{opt.label}</p>
                      <p className="text-xs text-muted-foreground">{opt.description}</p>
                    </div>
                  </DropdownMenuItem>
                </div>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </Tooltip>
      )}

      <ProGate feature={sendEmailFeature} mode="overlay-button">
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>
            <Button
              size="icon" className="h-10 w-10" variant="outline" aria-label={sendEmailLabel}
              onClick={onSendEmail}
              {...(tourTargets?.sendEmailButton ? { "data-tour-target": tourTargets.sendEmailButton } : {})}
            >
              <Mail className="w-5 h-5" strokeWidth={2.25} />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{sendEmailLabel}</TooltipContent>
        </Tooltip>
      </ProGate>

      {isSingleExport ? (
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>
            <Button size="icon" className="h-10 w-10" variant="outline" aria-label="Export PDF" onClick={exportOptions[0].onClick}>
              <Download className="w-5 h-5" strokeWidth={2.25} />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Export PDF</TooltipContent>
        </Tooltip>
      ) : (
        <Tooltip delayDuration={0}>
          <DropdownMenu>
            <TooltipTrigger asChild>
              <DropdownMenuTrigger asChild>
                <Button size="icon" className="h-10 w-10" variant="outline" aria-label="Export PDF">
                  <Download className="w-5 h-5" strokeWidth={2.25} />
                </Button>
              </DropdownMenuTrigger>
            </TooltipTrigger>
            <TooltipContent>Export PDF</TooltipContent>
            <DropdownMenuContent align="end" className="w-56">
              {exportOptions.map((opt, i) => (
                <div key={i}>
                  {i > 0 && <DropdownMenuSeparator />}
                  <DropdownMenuItem onClick={opt.onClick}>
                    {opt.icon}
                    <div>
                      <p className="font-medium">{opt.label}</p>
                      <p className="text-xs text-muted-foreground">{opt.description}</p>
                    </div>
                  </DropdownMenuItem>
                </div>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </Tooltip>
      )}

      {/* Secondary action buttons — always visible */}
      <div className="flex items-center gap-2">
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>
            <Button size="icon" className="h-10 w-10" variant="outline" aria-label={duplicateLabel} onClick={onDuplicate} disabled={isDuplicating}>
              {isDuplicating ? <Loader2 className="w-5 h-5 animate-spin" strokeWidth={2.25} /> : <Copy className="w-5 h-5" strokeWidth={2.25} />}
            </Button>
          </TooltipTrigger>
          <TooltipContent>{duplicateLabel}</TooltipContent>
        </Tooltip>

        {onEdit && (
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <Button size="icon" className="h-10 w-10" variant="outline" aria-label={editLabel} onClick={onEdit}>
                <Edit className="w-5 h-5" strokeWidth={2.25} />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{editLabel}</TooltipContent>
          </Tooltip>
        )}

        {onSaveAsTemplate && (
          isPro ? (
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <Button size="icon" className="h-10 w-10" variant="outline" aria-label="Save as Template" onClick={onSaveAsTemplate}>
                  <LayoutTemplate className="w-5 h-5" strokeWidth={2.25} />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Save as Template</TooltipContent>
            </Tooltip>
          ) : (
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <Button size="icon" className="h-10 w-10" variant="outline" aria-label="Save as Template (Pro)" asChild>
                  <Link href="/upgrade" className="flex items-center text-amber-700 dark:text-amber-400">
                    <Lock className="w-5 h-5" strokeWidth={2.25} />
                  </Link>
                </Button>
              </TooltipTrigger>
              <TooltipContent>Upgrade to Pro to unlock {PRO_FEATURES.TEMPLATES}</TooltipContent>
            </Tooltip>
          )
        )}

        {convertDesktopSlot}

        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>
            <Button size="icon" className="h-10 w-10 text-destructive hover:text-destructive hover:bg-destructive/10 border-destructive/30" variant="outline" aria-label={deleteLabel} onClick={onDelete}>
              <Trash2 className="w-5 h-5" strokeWidth={2.25} />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{deleteLabel}</TooltipContent>
        </Tooltip>
      </div>
    </div>
  );
}
